import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { useOrg } from "@/lib/states/session/hooks";
import { trpc } from "@/lib/trpc/client";
import { cn } from "@/lib/utils/ui";
import {
  Apple,
  BarChart3,
  BookOpen,
  ChefHat,
  ClipboardCheck,
  DollarSign,
  Dumbbell,
  FileText,
  Leaf,
  MessageCircle,
  Palette,
  Pill,
  Search,
  Settings,
  UserPlus,
  Users,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

type SearchItem = {
  id?: string;
  type: "client" | "workout" | "form" | "shortcut";
  label: string;
  description?: string;
  value: string;
  icon: typeof Search;
  action: () => void;
  avatarUrl?: string;
  avatarFallback?: string;
};

const SEARCH_TRIGGER_CLASSES =
  "flex h-9 w-44 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground shadow-sm transition hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:w-60";

const isEditableTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tag = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || target.isContentEditable;
};

const GlobalSearch = ({ iconOnly = false }: { iconOnly?: boolean }) => {
  const navigate = useNavigate();
  const org = useOrg();
  const [open, setOpen] = useState(false);
  const { data: searchData, isLoading } = trpc.search.global.useQuery(
    undefined,
    {
      enabled: open && Boolean(org?.id),
      staleTime: 60 * 1000,
    },
  );
  const shortcutLabel =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad|iPod/.test(navigator.platform)
      ? "⌘"
      : "Ctrl";

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "k") {
        return;
      }

      if (!(event.metaKey || event.ctrlKey)) {
        return;
      }

      if (isEditableTarget(event.target)) {
        return;
      }

      event.preventDefault();
      setOpen(true);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const shortcuts = useMemo<SearchItem[]>(
    () => [
      {
        type: "shortcut",
        label: "Clients",
        description: "Client list",
        value: "clients shortcuts",
        icon: Users,
        action: () => navigate({ to: "/clients" }),
      },
      {
        type: "shortcut",
        label: "Check-ins",
        description: "Client check-ins",
        value: "checkins shortcuts",
        icon: ClipboardCheck,
        action: () => navigate({ to: "/checkins" }),
      },
      {
        type: "shortcut",
        label: "Chat",
        description: "Messaging",
        value: "chat shortcuts",
        icon: MessageCircle,
        action: () => navigate({ to: "/chat" }),
      },
      {
        type: "shortcut",
        label: "Food",
        description: "Food database",
        value: "food shortcuts",
        icon: Apple,
        action: () => navigate({ to: "/foods" }),
      },
      {
        type: "shortcut",
        label: "Meals",
        description: "Meal plans",
        value: "meals shortcuts",
        icon: ChefHat,
        action: () => navigate({ to: "/meals" }),
      },
      {
        type: "shortcut",
        label: "Supplements",
        description: "Supplement library",
        value: "supplements shortcuts",
        icon: Pill,
        action: () => navigate({ to: "/supplements" }),
      },
      {
        type: "shortcut",
        label: "Exercises",
        description: "Exercise library",
        value: "exercises shortcuts",
        icon: Dumbbell,
        action: () => navigate({ to: "/exercises" }),
      },
      {
        type: "shortcut",
        label: "Workouts",
        description: "Workout templates",
        value: "workouts shortcuts",
        icon: BookOpen,
        action: () => navigate({ to: "/workouts" }),
      },
      {
        type: "shortcut",
        label: "Packages",
        description: "Payments and packages",
        value: "packages shortcuts",
        icon: DollarSign,
        action: () => navigate({ to: "/packages" }),
      },
      {
        type: "shortcut",
        label: "Branding",
        description: "Organisation branding",
        value: "branding shortcuts",
        icon: Palette,
        action: () => navigate({ to: "/branding" }),
      },
      {
        type: "shortcut",
        label: "Habits",
        description: "Habits library",
        value: "habits shortcuts",
        icon: Leaf,
        action: () => navigate({ to: "/habits" }),
      },
      {
        type: "shortcut",
        label: "Metrics",
        description: "Metrics library",
        value: "metrics shortcuts",
        icon: BarChart3,
        action: () => navigate({ to: "/metrics" }),
      },
      {
        type: "shortcut",
        label: "Forms",
        description: "Form library",
        value: "forms shortcuts",
        icon: FileText,
        action: () => navigate({ to: "/forms" }),
      },
      {
        type: "shortcut",
        label: "Leads",
        description: "Lead submissions",
        value: "leads shortcuts",
        icon: UserPlus,
        action: () => navigate({ to: "/leads" }),
      },
      {
        type: "shortcut",
        label: "Team",
        description: "Manage team",
        value: "team shortcuts",
        icon: Users,
        action: () => navigate({ to: "/team" }),
      },
      {
        type: "shortcut",
        label: "Settings",
        description: "Account settings",
        value: "settings shortcuts",
        icon: Settings,
        action: () => navigate({ to: "/settings" }),
      },
    ],
    [navigate],
  );
  const brandColor =
    org?.primaryColor && /^#[0-9a-fA-F]{6}$/.test(org.primaryColor)
      ? org.primaryColor
      : "#0051FF";
  const orgLogoUrl = org?.logoUrl ?? undefined;
  const orgInitials = useMemo(
    () =>
      (org?.name ?? "Trainrr")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase(),
    [org?.name],
  );

  const items = useMemo<SearchItem[]>(() => {
    if (!searchData) {
      return [];
    }

    const clientItems = searchData.clients.map((client) => {
      const firstName = client.first_name ?? "";
      const lastName = client.last_name ?? "";
      const email = client.email ?? "";
      const name = `${firstName} ${lastName}`.trim();
      const label = name || email || "Unnamed client";
      const value = `${label} ${email}`.trim();
      const fallback = label
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();

      return {
        id: client.id,
        type: "client" as const,
        label,
        description: email || undefined,
        value,
        icon: Users,
        avatarUrl: client.avatar?.url,
        avatarFallback: fallback || "CL",
        action: () =>
          navigate({
            to: "/clients/$clientId",
            params: { clientId: client.id },
          }),
      };
    });

    const workoutItems = searchData.workouts.map((workout) => {
      const label = workout.name ?? "Untitled workout";
      const value = `${label} ${workout.description ?? ""}`.trim();

      return {
        id: workout.id,
        type: "workout" as const,
        label,
        description: workout.description ?? undefined,
        value,
        icon: Dumbbell,
        action: () =>
          navigate({
            to: "/workouts/$workoutId",
            params: { workoutId: workout.id },
          }),
      };
    });

    const formItems = searchData.forms.map((form) => {
      const label = form.name ?? "Untitled form";
      const value = `${label} ${form.type ?? ""}`.trim();

      return {
        id: form.id,
        type: "form" as const,
        label,
        description: form.type ?? undefined,
        value,
        icon: FileText,
        action: () =>
          navigate({
            to: "/forms/$formId",
            params: { formId: form.id },
          }),
      };
    });

    return [...clientItems, ...workoutItems, ...formItems];
  }, [navigate, searchData]);

  const groupedItems = useMemo(
    () =>
      [
        { label: "Shortcuts", items: shortcuts },
        {
          label: "Clients",
          items: items.filter((item) => item.type === "client"),
        },
        {
          label: "Workouts",
          items: items.filter((item) => item.type === "workout"),
        },
        { label: "Forms", items: items.filter((item) => item.type === "form") },
      ].filter((group) => group.items.length > 0),
    [items, shortcuts],
  );

  const handleSelect = (item: SearchItem) => {
    item.action();
    setOpen(false);
  };

  return (
    <>
      {iconOnly ? (
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          onClick={() => setOpen(true)}
          aria-label="Open search"
        >
          <Search className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          className={cn(SEARCH_TRIGGER_CLASSES)}
          onClick={() => setOpen(true)}
          aria-label="Open search"
        >
          <Search className="h-4 w-4" />
          <span className="truncate">Search...</span>
          <span className="ml-auto hidden items-center gap-1 text-xs text-muted-foreground sm:flex">
            <KbdGroup>
              <Kbd>{shortcutLabel}K</Kbd>
            </KbdGroup>
          </span>
        </button>
      )}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <div className="border-b border-border/70 px-4 py-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 rounded-xl border border-white/70 shadow-sm">
              <AvatarImage src={orgLogoUrl} className="object-cover" />
              <AvatarFallback
                className="rounded-xl text-sm font-semibold text-white"
                style={{ backgroundColor: brandColor }}
              >
                {orgInitials || "T"}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">
                {org?.name ?? "Trainrr"}
              </p>
              <p className="text-xs text-muted-foreground">
                Search your clients, workouts, and forms
              </p>
            </div>
          </div>
        </div>
        <CommandInput
          placeholder="Search clients, workouts, forms..."
          autoFocus
        />
        <CommandList className="h-[360px] max-h-[360px] sm:h-[420px] sm:max-h-[420px]">
          <CommandEmpty>
            {isLoading ? "Loading results..." : "No results found."}
          </CommandEmpty>
          {groupedItems.map((group, index) => (
            <div key={group.label}>
              {index > 0 ? <CommandSeparator /> : null}
              <CommandGroup heading={group.label}>
                {group.items.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <CommandItem
                      key={`${item.type}-${item.id ?? item.value}-${index}`}
                      value={item.value}
                      onSelect={() => handleSelect(item)}
                      className="flex items-center gap-2"
                    >
                      {item.avatarUrl ? (
                        <Avatar className="h-7 w-7 rounded-md border">
                          <AvatarImage
                            src={item.avatarUrl}
                            className="object-cover"
                          />
                          <AvatarFallback className="rounded-md text-[10px]">
                            {item.avatarFallback}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <Icon className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="flex-1 truncate text-sm text-foreground">
                        {item.label}
                      </span>
                      {item.description ? (
                        <span
                          className={cn(
                            "truncate text-xs text-muted-foreground",
                            item.type === "workout"
                              ? "max-w-[220px]"
                              : "max-w-[160px]",
                          )}
                        >
                          {item.description}
                        </span>
                      ) : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </div>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default GlobalSearch;
